import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel
} from "@mui/material";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession
} from "amazon-cognito-identity-js";
import { useUser } from "../../contexts/UserContext";
// useAuth might be needed in the future for OIDC authentication
// import { useAuth } from "react-oidc-context";

// Define the VerifyAccount component
const VerifyAccount: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, setLoggedIn } = useUser();
  // auth from useAuth() might be needed in the future
  
  // Verification states
  const [email, setEmail] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [verificationMethod, setVerificationMethod] = useState<"email" | "sms">("email");
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  
  // Error and success states
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Get Cognito User Pool config from environment variables
  const userPoolId = process.env.REACT_APP_USER_POOL_ID || "";
  const clientId = process.env.REACT_APP_CLIENT_ID || "";

  const userPool = new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
  });

  const handleSnackClose = () => {
    setShowSnackbar(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleVerificationMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationMethod(event.target.value as "email" | "sms");
  };

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setShowSnackbar(true);
  };

  const showErrorMessage = (message: string) => {
    setError(message);
    setShowSnackbar(true);
  };

  // Verify account with code
  const handleVerify = () => {
    if (!email) {
      showErrorMessage("Please enter your email address");
      return;
    }

    if (!verificationCode) {
      showErrorMessage("Please enter the verification code");
      return;
    }

    setIsVerifying(true);
    setError("");
    setSuccessMessage("");

    const userData = {
      Username: email,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    cognitoUser.confirmRegistration(verificationCode, true, async (err) => {
      if (err) {
        showErrorMessage(err.message || "Failed to verify account. Please try again.");
        setIsVerifying(false);
        return;
      }

      showSuccessMessage("Account verified successfully!");

      // If password provided, attempt to authenticate user
      if (password) {
        try {
          const result = await authenticateUser();
          const idToken = result.getIdToken().getJwtToken();
          localStorage.setItem('id_token', idToken);
          localStorage.setItem('access_token', result.getAccessToken().getJwtToken());
          localStorage.setItem('refresh_token', result.getRefreshToken().getToken());

          // Update user context after successful authentication
          const userInfo = {
            username: email,
            email: email,
            token: idToken
          };
          setUser(userInfo);
          setLoggedIn(true);
          // Redirect to dashboard after successful verification and authentication
          setTimeout(() => {
            navigate("/dashboard");
          }, 1500);
        } catch (error) {
          showErrorMessage("Account verified, but automatic login failed. Please login manually.");
          setTimeout(() => {
            navigate("/login");
          }, 1500);
        }
      } else {
        // If no password provided, just direct to login
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      }
      
      setIsVerifying(false);
    });
  };

  // Function to authenticate user after confirmation
  const authenticateUser = async () => {
    if (!email || !password) {
      throw new Error("Email and password are required for authentication");
    }

    const authenticationData = {
      Username: email,
      Password: password
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    return new Promise<CognitoUserSession>((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (data) => {
          reject(new Error("New password required"));
        }
      });
    });
  };

  // Request new verification code
  const handleResendCode = () => {
    if (!email) {
      showErrorMessage("Please enter your email address");
      return;
    }

    setIsResending(true);
    setError("");
    setSuccessMessage("");

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    if (verificationMethod === "email") {
      // Resend confirmation code via email
      cognitoUser.resendConfirmationCode((err, result) => {
        setIsResending(false);
        if (err) {
          showErrorMessage(err.message || JSON.stringify(err));
        } else {
          showSuccessMessage("Confirmation code resent! Please check your email.");
        }
      });
    } else {
      // For SMS verification, we would need to set the user's phone number first
      // This is typically done during sign-up, but we can update the attribute here
      if (!phoneNumber) {
        showErrorMessage("Please enter your phone number");
        setIsResending(false);
        return;
      }

      // For SMS verification, we'd need to add the phone attribute and set delivery method
      // This is a simplified example - you may need additional Cognito API calls
      cognitoUser.getAttributeVerificationCode('phone_number', {
        onSuccess: () => {
          setIsResending(false);
          showSuccessMessage("Verification code sent to your phone number");
        },
        onFailure: (err) => {
          setIsResending(false);
          showErrorMessage(err.message || "Failed to send verification code to phone");
        },
        inputVerificationCode: () => {
          // This would be called when the code is sent successfully
          setIsResending(false);
          showSuccessMessage("Verification code sent to your phone number");
        }
      });
    }
  };

  // Styles
  const styles = {
    pageContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      minHeight: "100vh",
      py: 2,
    },
    paper: {
      p: 4,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    form: {
      width: "100%",
      mt: 1,
    },
    submit: {
      mt: 3,
      mb: 2,
    },
    tabPanel: {
      pt: 2,
      width: "100%",
    },
  };

  return (
    <Container maxWidth="sm">
      <Box sx={styles.pageContainer}>
        <Container component="main" maxWidth="xs">
          <Paper elevation={3} sx={styles.paper}>
            <Typography variant="h4" component="h1" gutterBottom>
              Verify Your Account
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Enter the verification code you received
            </Typography>

            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              centered
              sx={{ mb: 2, width: "100%" }}
            >
              <Tab label="Verify Code" />
              <Tab label="Request New Code" />
            </Tabs>

            <Box sx={styles.tabPanel} hidden={tabValue !== 0}>
              <Box component="form" noValidate sx={styles.form}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  fullWidth
                  required
                  autoComplete="email"
                />
                <TextField
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  margin="normal"
                  fullWidth
                  required
                />
                <TextField
                  label="Password (for immediate login)"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  fullWidth
                  helperText="Optional. Provide your password to log in immediately after verification."
                />
                <Button
                  variant="contained"
                  fullWidth
                  sx={styles.submit}
                  onClick={handleVerify}
                  disabled={isVerifying || !email || !verificationCode}
                >
                  {isVerifying ? <CircularProgress size={24} /> : "Verify Account"}
                </Button>
              </Box>
            </Box>

            <Box sx={styles.tabPanel} hidden={tabValue !== 1}>
              <Box component="form" noValidate sx={styles.form}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  fullWidth
                  required
                  autoComplete="email"
                />
                
                <FormControl component="fieldset" sx={{ mt: 2, mb: 1 }}>
                  <FormLabel component="legend">Verification Method</FormLabel>
                  <RadioGroup
                    value={verificationMethod}
                    onChange={handleVerificationMethodChange}
                  >
                    <FormControlLabel value="email" control={<Radio />} label="Email" />
                    <FormControlLabel value="sms" control={<Radio />} label="SMS" />
                  </RadioGroup>
                </FormControl>

                {verificationMethod === "sms" && (
                  <TextField
                    label="Phone Number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    margin="normal"
                    fullWidth
                    required
                    placeholder="+1XXXXXXXXXX"
                    helperText="Enter phone number with country code (e.g., +12125551234)"
                  />
                )}

                <Button
                  variant="contained"
                  fullWidth
                  sx={styles.submit}
                  onClick={handleResendCode}
                  disabled={isResending || !email || (verificationMethod === "sms" && !phoneNumber)}
                >
                  {isResending ? <CircularProgress size={24} /> : "Request New Code"}
                </Button>
              </Box>
            </Box>

            <Button
              color="primary"
              fullWidth
              onClick={() => navigate("/login")}
              sx={{ mt: 2 }}
            >
              Back to Login
            </Button>
          </Paper>
        </Container>
      </Box>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          onClose={handleSnackClose} 
          severity={error ? "error" : "success"} 
          sx={{ width: "100%" }}
        >
          {error || successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VerifyAccount;
